import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Loader2, Upload, X, ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { PortalHeader, PortalEmpty, PortalCard } from "@/components/portal/PortalHeader";
import { getMyProject, listMyGallery, addMyGalleryPhoto, getMyFileSignedUrl } from "@/lib/portal/portal.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/portal/gallery")({
  component: GalleryPage,
});

const ALBUMS: Array<{ id: "all" | "inspiration" | "before" | "progress" | "after"; label: string }> = [
  { id: "all", label: "All" },
  { id: "inspiration", label: "Inspiration" },
  { id: "before", label: "Before" },
  { id: "progress", label: "Progress" },
  { id: "after", label: "After" },
];

function ResolvedImg({ projectId, src, onClick }: { projectId: string; src: string; onClick?: () => void }) {
  const isHttp = /^https?:\/\//i.test(src);
  const signFn = useServerFn(getMyFileSignedUrl);
  const q = useQuery({
    queryKey: ["portal", "sign", projectId, src],
    queryFn: () => signFn({ data: { projectId, path: src } }),
    enabled: !isHttp,
    staleTime: 4 * 60 * 1000,
  });
  const url = isHttp ? src : q.data?.url;
  if (!url) return <div className="aspect-square bg-white/[0.03] animate-pulse" />;
  return <img src={url} alt="" loading="lazy" onClick={onClick} className="aspect-square w-full object-cover cursor-zoom-in hover:opacity-90 transition" />;
}

function GalleryPage() {
  const qc = useQueryClient();
  const projectFn = useServerFn(getMyProject);
  const listFn = useServerFn(listMyGallery);
  const addFn = useServerFn(addMyGalleryPhoto);
  const signFn = useServerFn(getMyFileSignedUrl);

  const projectQ = useQuery({ queryKey: ["portal", "project"], queryFn: () => projectFn() });
  const projectId = projectQ.data?.project?.id;
  const galleryQ = useQuery({
    queryKey: ["portal", "gallery", projectId],
    queryFn: () => listFn({ data: { projectId: projectId! } }),
    enabled: !!projectId,
  });

  const [activeAlbum, setActiveAlbum] = useState<typeof ALBUMS[number]["id"]>("all");
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState<number | null>(null);

  const filtered = useMemo(() => {
    const photos = galleryQ.data ?? [];
    return activeAlbum === "all" ? photos : photos.filter((p) => p.album === activeAlbum);
  }, [galleryQ.data, activeAlbum]);

  if (projectQ.isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!projectId) return <PortalEmpty title="No project yet" />;

  const handleUpload = async (file: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${projectId}/gallery/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("project-files").upload(path, file, {
        contentType: file.type, upsert: false,
      });
      if (error) throw error;
      await addFn({ data: { projectId, album: "inspiration", url: path } });
      await qc.invalidateQueries({ queryKey: ["portal", "gallery", projectId] });
    } catch (e: any) {
      alert(e?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openViewer = async (i: number) => setViewer(i);
  const closeViewer = () => setViewer(null);

  return (
    <>
      <PortalHeader
        eyebrow="Photo Gallery"
        title="Project photos"
        description="Upload inspiration shots — your project manager and painter will post progress and finished photos here."
        actions={
          <label className="cursor-pointer inline-flex items-center gap-2 rounded-full bg-amber-300 hover:bg-amber-200 text-zinc-950 px-4 py-2 text-xs font-semibold">
            {uploading ? <Loader2 className="size-3.5 animate-spin" /> : <Upload className="size-3.5" />}
            Upload inspiration
            <input type="file" accept="image/*" className="hidden" disabled={uploading}
              onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0])} />
          </label>
        }
      />

      <div className="flex gap-1.5 flex-wrap mb-6">
        {ALBUMS.map((a) => (
          <button key={a.id} onClick={() => setActiveAlbum(a.id)}
            className={`text-xs px-3.5 py-1.5 rounded-full border transition ${activeAlbum === a.id ? "bg-amber-300 text-zinc-950 border-amber-300 font-semibold" : "border-white/10 text-zinc-400 hover:text-white"}`}>
            {a.label}
          </button>
        ))}
      </div>

      {galleryQ.isLoading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="size-5 animate-spin text-amber-300" /></div>
      ) : filtered.length === 0 ? (
        <PortalEmpty title="No photos in this album yet" description="Photos uploaded by you or your team will appear here." />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {filtered.map((p, i) => (
            <PortalCard key={p.id} className="overflow-hidden p-0 relative">
              <ResolvedImg projectId={projectId} src={p.thumb_url || p.url} onClick={() => openViewer(i)} />
              <span className="absolute top-2 left-2 text-[9px] uppercase tracking-wider bg-black/60 backdrop-blur px-2 py-0.5 rounded-full text-amber-300 font-semibold">
                {p.album}
              </span>
            </PortalCard>
          ))}
        </div>
      )}

      {viewer != null && filtered[viewer] && (
        <Viewer
          projectId={projectId}
          items={filtered}
          index={viewer}
          onClose={closeViewer}
          onPrev={() => setViewer((v) => (v! > 0 ? v! - 1 : filtered.length - 1))}
          onNext={() => setViewer((v) => (v! < filtered.length - 1 ? v! + 1 : 0))}
        />
      )}
    </>
  );
}

function Viewer({ projectId, items, index, onClose, onPrev, onNext }: {
  projectId: string; items: any[]; index: number; onClose: () => void; onPrev: () => void; onNext: () => void;
}) {
  const p = items[index];
  const isHttp = /^https?:\/\//i.test(p.url);
  const signFn = useServerFn(getMyFileSignedUrl);
  const q = useQuery({
    queryKey: ["portal", "sign", projectId, p.url],
    queryFn: () => signFn({ data: { projectId, path: p.url } }),
    enabled: !isHttp,
  });
  const url = isHttp ? p.url : q.data?.url;

  return (
    <div className="fixed inset-0 bg-black/95 z-50 grid place-items-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 size-10 rounded-full bg-white/10 grid place-items-center text-white"><X /></button>
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 size-12 rounded-full bg-white/10 grid place-items-center text-white hover:bg-white/20"><ChevronLeft /></button>
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 size-12 rounded-full bg-white/10 grid place-items-center text-white hover:bg-white/20"><ChevronRight /></button>
      <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        {url ? <img src={url} alt="" className="max-w-full max-h-[85vh] object-contain rounded-xl" /> : <Loader2 className="size-6 animate-spin text-amber-300" />}
        <p className="mt-3 text-center text-xs text-zinc-400">{p.caption || `${p.album} · ${new Date(p.created_at).toLocaleDateString()}`}</p>
      </div>
    </div>
  );
}